/**
 * SandboxDisclosure — Full-screen disclosure shown on first login to a sandbox environment.
 * No confirmation required — user clicks "Continue to Sandbox" to dismiss.
 * Stores acknowledgment in localStorage so it only shows once per browser session.
 */

import { useState, useEffect } from 'react';
import { useBranding } from '@/contexts/BrandingContext';
import { AlertTriangle, Shield, Database, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'sandbox_disclosure_seen';

export default function SandboxDisclosure() {
  const { isSandbox, sandboxExpiresAt } = useBranding();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSandbox) return;

    const cboId = localStorage.getItem('active_cbo_id') || '';
    const seenKey = `${STORAGE_KEY}_${cboId}`;
    const alreadySeen = localStorage.getItem(seenKey);

    if (!alreadySeen) {
      setVisible(true);
    }
  }, [isSandbox]);

  if (!visible) return null;

  const handleContinue = () => {
    const cboId = localStorage.getItem('active_cbo_id') || '';
    localStorage.setItem(`${STORAGE_KEY}_${cboId}`, new Date().toISOString());
    setVisible(false);
  };

  let daysRemaining: number | null = null;
  if (sandboxExpiresAt) {
    const expires = new Date(sandboxExpiresAt);
    daysRemaining = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Sandbox Environment</h1>
          <p className="text-amber-100 mt-1 text-sm">Demo & Evaluation Instance</p>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-5">
          <p className="text-gray-700 text-base leading-relaxed">
            Welcome to the WeaveIn.ai sandbox environment. This instance is provided for
            demonstration and evaluation purposes only.
          </p>

          <div className="space-y-3">
            <div className="flex gap-3 items-start">
              <Database className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Sample Data Only</p>
                <p className="text-sm text-gray-500">
                  This environment contains pre-populated sample data for demonstration.
                  All member names, records, and transactions are fictional. Do not enter
                  real Protected Health Information (PHI) or personally identifiable data.
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-start">
              <Shield className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">No Performance Guarantees</p>
                <p className="text-sm text-gray-500">
                  Sandbox functionality and execution are not subject to any performance
                  standards, service level agreements, or uptime guarantees. Features may
                  behave differently than in production environments.
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-start">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Limited Duration
                  {daysRemaining !== null && (
                    <span className="ml-2 text-amber-600 font-normal">
                      ({daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining)
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  This sandbox environment has a limited trial period. Data may be reset
                  or the environment deactivated after the trial expires. External
                  communications (SMS, email) are simulated and not delivered.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-xs text-amber-800">
              By continuing, you acknowledge that this is a sandbox environment intended
              solely for evaluation and demonstration of platform capabilities.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t flex justify-end">
          <Button
            onClick={handleContinue}
            className="bg-amber-500 hover:bg-amber-600 text-white px-6"
          >
            Continue to Sandbox <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
